import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent, interval } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { buildPaginationItems, buildPaginationLabel, extractPagination } from '../../../helper/table-utils';
import { Httpservice } from '../../../services/httpservice';
import { AppPreferencesService } from '../../../services/app-preferences';
import { ServerTimeService } from '../../../services/server-time';
import { CustomTooltipComponent } from '../../../helper/custom-helper/custom-tooltip';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import {
  AD_MONTH_OPTIONS,
  BS_MONTH_OPTIONS,
  buildAccordionLabel,
  buildDayLabel,
  buildMonthLabel,
  buildReportDateValue,
  getCurrentMonthValue,
} from './data-validation-report.data';
import {
  DataValidationBlockDefinition,
  DataValidationBlockVariationDetail,
  DataValidationDateConversionResponse,
  DataValidationDayCell,
  DataValidationDayDefinition,
  DataValidationPagination,
  DataValidationReportResponse,
  DataValidationStatusCounts,
  DataValidationSummaryResponse,
  DataValidationVerificationResponse,
  DataValidationVerificationStats,
  DataValidationSubstationOption,
  DataValidationSubstationRow,
  DataValidationStatus,
} from './data-validation-report.interfaces';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../../master/manage-generator/manage-generator-autocomplete';
import {
  commitReportAutocompleteValue,
  filterReportOptions,
  findReportOptionByLabel,
  handleReportAutocompleteSearch,
} from '../../reports/shared/report-ui.helpers';

const DATA_VALIDATION_DEFAULT_PAGE_SIZE = 15;
const DATA_VALIDATION_NO_RECORDS_LABEL = 'No records';
const DATA_VALIDATION_SHOWING_RESULTS_LABEL = 'Showing';
const DATA_VALIDATION_RESULTS_LABEL = 'generators';
const DATA_VALIDATION_OWNER_ALL_VALUE = 'all';
const DATA_VALIDATION_AVAILABILITY_ALL_VALUE = 'all';
type DataValidationAvailabilityFilter = DataValidationStatus | typeof DATA_VALIDATION_AVAILABILITY_ALL_VALUE;

@Component({
  selector: 'app-data-validation-report-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ReportShellComponent,
    ManageGeneratorAutocompleteComponent,
    CustomTooltipComponent,
  ],
  templateUrl: './data-validation-report.html',
  styleUrl: './data-validation-report.scss',
})
export class DataValidationReportPage implements OnInit {
protected readonly summaryStatusOrder: DataValidationStatus[] = ['FA', 'PA', 'NA'];
protected readonly verificationStatsSections = [
  { key: 'actual_capacity' as const, label: 'Expected Power (MW)', suffix: '' },
  { key: 'generating_capacity' as const, label: 'Actual Power', suffix: '' },
  { key: 'verification' as const, label: 'Variation', suffix: '%' },
];
protected get monthlyStatsTitle(): string {
  return `Monthly Stats (as on ${this.monthLabel || this.getMonthDisplayLabel()})`;
}

protected get monthlyStatsTitlePrefix(): string {
  return 'Monthly Stats (as on ';
}

protected get monthlyStatsDateLabel(): string {
  return this.monthLabel || this.getMonthDisplayLabel();
}

protected get dailyStatsTitle(): string {
  return `Daily Stats (as on ${this.dailyStatsLabel || (this.dailyStatsDate ? this.formatDateLabel(this.dailyStatsDate) : 'DD-MMM-YYYY')})`;
}

protected get dailyStatsTitlePrefix(): string {
  return 'Daily Stats (as on ';
}

protected get dailyStatsDateLabel(): string {
  return this.dailyStatsLabel || (this.dailyStatsDate ? this.formatDateLabel(this.dailyStatsDate) : 'DD-MMM-YYYY');
}

protected get dataVerificationTitle(): string {
  return `Data Verification Of Generators (as on ${this.dailyStatsDateLabel})`;
}

protected monthOptions = [...AD_MONTH_OPTIONS];
protected yearOptions: number[];
protected readonly monthNameControl: FormControl;
protected readonly yearControl: FormControl;
protected ownerOptions = [{ label: 'All Owners', value: DATA_VALIDATION_OWNER_ALL_VALUE }];
protected readonly availabilityOptions: { label: string; value: DataValidationAvailabilityFilter }[] = [
  { label: 'All', value: DATA_VALIDATION_AVAILABILITY_ALL_VALUE },
  { label: 'FA', value: 'FA' },
  { label: 'PA', value: 'PA' },
  { label: 'NA', value: 'NA' },
];




  protected substationOptions: DataValidationSubstationOption[] = [];
  protected filteredSubstationOptions: DataValidationSubstationOption[] = [];
  protected substationSearchTerm = '';
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly availabilityControl = new FormControl<DataValidationAvailabilityFilter>(DATA_VALIDATION_AVAILABILITY_ALL_VALUE);
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;

  protected monthLabel = '';
  protected days: DataValidationDayDefinition[] = [];
  protected blocks: DataValidationBlockDefinition[] = [];
  protected rows: DataValidationSubstationRow[] = [];
  protected filteredRows: DataValidationSubstationRow[] = [];
  protected monthlyStats: DataValidationStatusCounts = { FA: 0, PA: 0, NA: 0 };
  protected dailyStats: DataValidationStatusCounts = { FA: 0, PA: 0, NA: 0 };
  protected dataVerification: DataValidationVerificationStats = {
    actual_capacity: 0,
    generating_capacity: 0,
    verification: 0,
  };
  protected blockVariationDetailsByCellKey: Record<string, DataValidationBlockVariationDetail[]> = {};
  protected variationThreshold = 0;
  protected currentBlockNo: number | null = null;
  protected dailyStatsDate: string | null = null;
  protected dailyStatsLabel = '';
  protected dataVerificationDate: string | null = null;
  protected currentDateMode: 'AD' | 'BS' = 'AD';
  protected pagination: DataValidationPagination = {
    page_no: 1,
    page_size: DATA_VALIDATION_DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  };

  protected expandedCellKey: string | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly serverTimeSvc: ServerTimeService,
  ) {
    this.filtersForm = this.fb.group({
      month: [getCurrentMonthValue(), Validators.required],
      owner: [DATA_VALIDATION_OWNER_ALL_VALUE],
      substation_id: [null],
      page_size: [DATA_VALIDATION_DEFAULT_PAGE_SIZE],
    });
    this.pageSizeControl = this.filtersForm.get('page_size') as FormControl;

    const currentMonthValue = getCurrentMonthValue();
    const [initialYear, initialMonth] = currentMonthValue.split('-').map((part) => Number(part));

    const currentYear = new Date().getFullYear();
    this.yearOptions = Array.from({ length: 11 }, (_, index) => currentYear - 5 + index);

    this.monthNameControl = new FormControl(initialMonth);
    this.yearControl = new FormControl(initialYear);
  }

  async ngOnInit(): Promise<void> {
    this.bindFilterListeners();
    this.listenToPreferenceChanges();
    this.loadSubstationOptions();
    await this.syncMonthYearFilterMode();
    this.loadReport(1);
     this.setupLiveBlockTicker();
  }

  

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected async clearFilters(): Promise<void> {
    await this.resetMonthSelectionForCurrentMode();
    this.filtersForm.patchValue({ owner: DATA_VALIDATION_OWNER_ALL_VALUE }, { emitEvent: false });
    this.availabilityControl.setValue(DATA_VALIDATION_AVAILABILITY_ALL_VALUE, { emitEvent: false });
    this.expandedCellKey = null;
    this.substationSearchTerm = '';
    this.applySubstationAutocompleteFilters('');
    this.loadReport(1);
  }

  private applyAvailabilityFilter(): void {
    this.filteredRows = [...this.rows];
  }

  protected trackByStation(_: number, item: DataValidationSubstationRow): number {
    return item.station_id;
  }

  protected trackByDay(_: number, item: DataValidationDayDefinition): number {
    return item.day;
  }

  protected trackByBlock(_: number, item: DataValidationBlockDefinition): number {
    return item.block_no;
  }

  protected getDayHeaderClass(day: DataValidationDayDefinition): string {
    return [
      'data-validation-table__day-header',
      day.is_today ? 'data-validation-table__day-header--today' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  protected getCurrentDayOfMonth(): number | null {
    return this.days.find((day) => day.is_today)?.day ?? null;
  }

  protected getMonthDisplayLabel(): string {
    if (this.monthLabel) {
      return this.monthLabel;
    }

    if (this.currentDateMode === 'BS') {
      return this.getMonthYearLabelFromFilterControls();
    }

    return buildMonthLabel(this.getSelectedMonthValue());
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(
      this.pagination,
      DATA_VALIDATION_NO_RECORDS_LABEL,
      DATA_VALIDATION_SHOWING_RESULTS_LABEL,
      DATA_VALIDATION_RESULTS_LABEL,
    );
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected getSummaryCounts(): DataValidationStatusCounts {
    return this.monthlyStats;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected goToPage(pageNo: number): void {
    if (this.isLoading || pageNo === this.pagination.page_no) {
      return;
    }

    this.loadReport(pageNo);
  }

  protected goToPreviousPage(): void {
    if (this.pagination.page_no <= 1) {
      return;
    }

    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    if (this.pagination.page_no >= this.pagination.total_pages || this.pagination.total_pages === 0) {
      return;
    }

    this.goToPage(this.pagination.page_no + 1);
  }

  protected getMatrixColspan(): number {
    return Math.max(this.days.length + 1, 1);
  }

  protected getCellStatusClass(status: DataValidationStatus): string {
    return `data-validation-table__status-cell--${status.toLowerCase()}`;
  }

  protected getStatusButtonClass(status: DataValidationStatus, active: boolean): string {
    return [
      'data-validation-table__status-button',
      this.getCellStatusClass(status),
      active ? 'data-validation-table__status-button--active' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  protected getCellTitle(cell: DataValidationDayCell): string {
    switch (cell.status) {
      case 'FA':
        return `Full Available (${cell.available_blocks}/${cell.expected_blocks || cell.available_blocks})`;
      case 'PA':
        return `Partial Available (${cell.available_blocks}/${cell.expected_blocks || this.blocks.length})`;
      default:
        return 'Not Available';
    }
  }

  protected shouldShowAvailabilityCell(cell: DataValidationDayCell): boolean {
    const selectedAvailability = this.availabilityControl.value ?? DATA_VALIDATION_AVAILABILITY_ALL_VALUE;
    return selectedAvailability === DATA_VALIDATION_AVAILABILITY_ALL_VALUE || cell.status === selectedAvailability;
  }

  protected isCellExpanded(row: DataValidationSubstationRow, cell: DataValidationDayCell): boolean {
    return this.expandedCellKey === this.createCellKey(row.station_id, cell.day);
  }

  protected async toggleCell(row: DataValidationSubstationRow, cell: DataValidationDayCell): Promise<void> {
    const key = this.createCellKey(row.station_id, cell.day);
    if (this.expandedCellKey === key) {
      this.expandedCellKey = null;
      return;
    }

    const convertedDate = await this.resolveDateForCurrentMode(cell.date);
    const selectedDate = convertedDate?.selectedDate ?? cell.date;
    const selectedDateLabel = convertedDate?.selectedDateLabel ?? this.formatDateLabel(cell.date);
    this.loadDailyStats(selectedDate, selectedDateLabel);
    this.loadVerificationStats(selectedDate, row.station_id, key);
    this.expandedCellKey = key;
  }

  protected getExpandedCellForRow(row: DataValidationSubstationRow): DataValidationDayCell | null {
    if (!this.expandedCellKey) {
      return null;
    }

    const [stationId, day] = this.expandedCellKey.split('-').map((part) => Number(part));
    if (stationId !== row.station_id || !Number.isFinite(day)) {
      return null;
    }

    return row.cells.find((cell) => cell.day === day) ?? null;
  }

  protected getAccordionLabel(cell: DataValidationDayCell): string {
    return buildAccordionLabel(cell.day, this.getMonthDisplayLabel());
  }

  protected getAccordionBadgeClass(status: DataValidationStatus): string {
    return `data-validation-accordion__badge data-validation-accordion__badge--${status.toLowerCase()}`;
  }

  protected getAccordionValue(cell: DataValidationDayCell, blockNo: number): string {
    const blockValue = cell.blocks.find((item) => item.block_no === blockNo)?.value;
    if (blockValue === null || blockValue === undefined) {
      return 'N/A';
    }

    return this.formatNumber(blockValue);
  }

  protected getBlockVariationDetail(
    row: DataValidationSubstationRow,
    cell: DataValidationDayCell,
    blockNo: number,
  ): DataValidationBlockVariationDetail | null {
    const cellKey = this.createCellKey(row.station_id, cell.day);
    return this.blockVariationDetailsByCellKey[cellKey]?.find((detail) => detail.block_no === blockNo) ?? null;
  }

  protected formatDayHeaderLabel(day: DataValidationDayDefinition): string {
    return day.label || String(day.day);
  }

  protected formatText(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    const text = String(value).trim();
    return text || 'N/A';
  }

  protected getEmptyStateColspan(): number {
    return Math.max(this.days.length + 1, 1);
  }

  protected isCurrentBlockHour(blockNo: number): boolean {
    const currentBlockNo = this.getCurrentBlockNo();
    return currentBlockNo !== null && currentBlockNo === blockNo;
  }
  private bindFilterListeners(): void {
    this.monthNameControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncMonthValue());

    this.yearControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncMonthValue());

    this.filtersForm.get('owner')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.substationSearchTerm = '';
        if (this.normalizeNumber(this.filtersForm.get('substation_id')?.value) !== null) {
          this.filtersForm.patchValue({ substation_id: null }, { emitEvent: false });
        }
        this.applySubstationAutocompleteFilters('');
      });

    this.filtersForm.valueChanges
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadReport(1);
    });

    this.availabilityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.expandedCellKey = null;
        this.loadReport(1);
      });
}

  private syncMonthValue(): void {
    const year = this.yearControl.value;
    const month = String(this.monthNameControl.value).padStart(2, '0');
    this.filtersForm.patchValue({ month: `${year}-${month}` });
  }

  private async resetMonthSelectionForCurrentMode(): Promise<void> {
    const targetMode = this.getDatePreferenceKey();
    const nextMonthValue = this.getCurrentMonthValueForMode(targetMode);
    this.currentDateMode = targetMode;
    this.setMonthYearOptionsForMode(targetMode, nextMonthValue);
    const [year, month] = nextMonthValue.split('-').map((value) => Number(value));
    this.monthNameControl.setValue(month, { emitEvent: false });
    this.yearControl.setValue(year, { emitEvent: false });
    this.filtersForm.patchValue(
      {
        month: nextMonthValue,
        substation_id: null,
        page_size: DATA_VALIDATION_DEFAULT_PAGE_SIZE,
      },
      { emitEvent: false },
    );
  }

  private async syncMonthYearFilterMode(): Promise<void> {
    const targetMode = this.getDatePreferenceKey();
    const selectedMonthValue = this.getSelectedMonthValue();
    const currentMode = this.currentDateMode;
    const currentMonthValueInCurrentMode = this.getCurrentMonthValueForMode(currentMode);

    let nextMonthValue = selectedMonthValue;
    if (currentMode !== targetMode) {
      if (selectedMonthValue === currentMonthValueInCurrentMode) {
        nextMonthValue = this.getCurrentMonthValueForMode(targetMode);
      } else {
        nextMonthValue = await this.convertMonthValueForMode(selectedMonthValue, targetMode, currentMode);
      }
    }

    this.currentDateMode = targetMode;
    this.setMonthYearOptionsForMode(targetMode, nextMonthValue);
    if (nextMonthValue) {
      const [year, month] = nextMonthValue.split('-').map((value) => Number(value));
      this.monthNameControl.setValue(month, { emitEvent: false });
      this.yearControl.setValue(year, { emitEvent: false });
      this.filtersForm.patchValue({ month: nextMonthValue }, { emitEvent: false });
    }
  }

  private setMonthYearOptionsForMode(mode: 'AD' | 'BS', currentMonthValue: string): void {
    if (mode === 'BS') {
      this.monthOptions = [...BS_MONTH_OPTIONS];
      const bsYear = this.getYearFromMonthValue(currentMonthValue);
      this.yearOptions = Array.from({ length: 11 }, (_, index) => bsYear - 5 + index);
      return;
    }

    this.monthOptions = [...AD_MONTH_OPTIONS];
    const adYear = this.getYearFromMonthValue(currentMonthValue) || new Date().getFullYear();
    this.yearOptions = Array.from({ length: 11 }, (_, index) => adYear - 5 + index);
  }

  private getYearFromMonthValue(monthValue: string): number {
    const year = Number(String(monthValue ?? '').split('-')[0]);
    return Number.isFinite(year) ? year : new Date().getFullYear();
  }

  private getCurrentMonthValueForMode(mode: 'AD' | 'BS'): string {
    if (mode === 'BS') {
      const currentBsDateValue = this.serverTimeSvc.getCurrentBsDateValue();
      if (currentBsDateValue && /^\d{4}-\d{2}-\d{2}$/.test(currentBsDateValue)) {
        return currentBsDateValue.slice(0, 7);
      }
    }

    return getCurrentMonthValue();
  }

  private async convertMonthValueForMode(
    monthValue: string,
    targetMode: 'AD' | 'BS',
    currentMode: 'AD' | 'BS',
  ): Promise<string> {
    const normalizedMonthValue = this.normalizeMonthValue(monthValue, currentMode);
    if (!normalizedMonthValue) {
      return getCurrentMonthValue();
    }

    const conversion = await this.convertDataValidationDate(`${normalizedMonthValue}-01`, currentMode);
    if (!conversion) {
      return normalizedMonthValue;
    }

    const converted = targetMode === 'BS' ? conversion.bs_date : conversion.ad_date;
    return this.toMonthValue(converted) || normalizedMonthValue;
  }

  private async convertDateForMode(dateValue: string, targetMode: 'AD' | 'BS'): Promise<string> {
    const conversion = await this.convertDataValidationDate(dateValue, targetMode === 'BS' ? 'AD' : 'BS');
    if (!conversion) {
      return dateValue;
    }

    return targetMode === 'BS' ? conversion.bs_date : conversion.ad_date;
  }

  private async convertDataValidationDate(
    dateValue: string,
    bsAdKey: 'AD' | 'BS',
  ): Promise<DataValidationDateConversionResponse | null> {
    const normalizedDate = this.normalizeDateValue(dateValue);
    if (!normalizedDate) {
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_DATA_VALIDATION_DATE,
          ApiMethod.POST,
          {
            data_date: normalizedDate,
            bs_ad_key: bsAdKey,
          },
          false,
          { suppressToast: true },
        ),
      );
      const payloadValue = this.extractResponseValue<DataValidationDateConversionResponse>(response);
      if (!payloadValue) {
        return null;
      }

      return payloadValue;
    } catch {
      return null;
    }
  }

  private normalizeDateValue(value: string): string | null {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return null;
    }

    return normalized;
  }

  private normalizeMonthValue(monthValue: string, mode: 'AD' | 'BS'): string | null {
    const normalized = this.normalizeDateValue(monthValue);
    if (!normalized) {
      return null;
    }

    const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(normalized);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month)) {
      return null;
    }

    if (mode === 'AD' && match[3]) {
      return `${match[1]}-${match[2]}`;
    }

    return `${match[1]}-${match[2]}`;
  }

  private toMonthValue(dateValue: string): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.normalizeDateValue(dateValue) ?? '');
    if (!match) {
      return null;
    }

    return `${match[1]}-${match[2]}`;
  }

  private getMonthYearLabelFromFilterControls(): string {
    const year = this.yearControl.value;
    const month = Number(this.monthNameControl.value);
    const monthLabel = this.monthOptions.find((option) => option.value === month)?.label ?? '';
    if (!monthLabel || !year) {
      return this.monthLabel || '';
    }

    return `${monthLabel} ${year}`;
  }

  private loadReport(pageNo = this.pagination.page_no): void {
    const monthValue = this.getSelectedMonthValue();
    if (!monthValue) {
      return;
    }

    this.isLoading = true;
    this.expandedCellKey = null;
    this.blockVariationDetailsByCellKey = {};
    const payload = {
      data_date: buildReportDateValue(monthValue),
      owner: this.getSelectedOwnerValue(),
      generator_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      page_no: pageNo,
      page_size: this.pageSizeControl.value ?? DATA_VALIDATION_DEFAULT_PAGE_SIZE,
      bs_ad_key: this.getDatePreferenceKey(),
      availability: this.getSelectedAvailabilityValue(),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          const payloadValue = this.extractResponseValue<DataValidationReportResponse>(response);
          this.monthLabel = payloadValue?.month_label ?? this.formatMonthValueLabel(monthValue);
          this.days = payloadValue?.days ?? [];
          this.blocks = payloadValue?.blocks ?? [];
          this.rows = payloadValue?.rows ?? [];
          this.applyAvailabilityFilter();
          this.monthlyStats = payloadValue?.monthly_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dailyStats = payloadValue?.daily_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dataVerification =
            payloadValue?.data_verification ?? {
              actual_capacity: 0,
              generating_capacity: 0,
              verification: 0,
            };
          this.variationThreshold = payloadValue?.variation_threshold ?? 0;
          this.currentBlockNo = payloadValue?.current_block_no ?? null;
          this.dailyStatsDate = payloadValue?.daily_stats_date ?? null;
          this.dailyStatsLabel = payloadValue?.daily_stats_label ?? '';
          this.dataVerificationDate = this.dailyStatsDate;
          this.pagination =
            payloadValue?.pagination ??
            extractPagination<DataValidationPagination>(response) ??
            extractPagination<DataValidationPagination>(payloadValue) ??
            {
              page_no: pageNo,
              page_size: Number(this.pageSizeControl.value ?? DATA_VALIDATION_DEFAULT_PAGE_SIZE),
              total_records: 0,
              total_pages: 0,
            };
        },
        error: (error) => {
          console.error('Failed to load data validation report', error);
          this.monthLabel = this.formatMonthValueLabel(monthValue);
          this.days = this.buildDaysFromMonth(monthValue);
          this.blocks = [];
          this.rows = [];
          this.applyAvailabilityFilter();
          this.monthlyStats = { FA: 0, PA: 0, NA: 0 };
          this.dailyStats = { FA: 0, PA: 0, NA: 0 };
          this.dataVerification = { actual_capacity: 0, generating_capacity: 0, verification: 0 };
          this.variationThreshold = 0;
          this.currentBlockNo = null;
          this.dailyStatsDate = null;
          this.dailyStatsLabel = '';
          this.dataVerificationDate = null;
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.pageSizeControl.value ?? DATA_VALIDATION_DEFAULT_PAGE_SIZE),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  private loadDailyStats(selectedDate: string, selectedDateLabel?: string): void {
    const monthValue = this.getSelectedMonthValue();
    if (!monthValue || !selectedDate) {
      return;
    }

    const requestedDate = selectedDate;
    this.dailyStatsDate = requestedDate;
    this.dailyStatsLabel = selectedDateLabel || this.formatDateLabel(requestedDate);

    const payload = {
      data_date: buildReportDateValue(monthValue),
      owner: this.getSelectedOwnerValue(),
      generator_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      selected_date: requestedDate,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_STATS, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const payloadValue = this.extractResponseValue<DataValidationSummaryResponse>(response);
          if (!payloadValue || this.dailyStatsDate !== requestedDate) {
            return;
          }

          this.monthlyStats = payloadValue.monthly_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dailyStats = payloadValue.daily_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dailyStatsDate = payloadValue.daily_stats_date ?? selectedDate;
          this.dailyStatsLabel = payloadValue.daily_stats_label ?? selectedDateLabel ?? this.formatDateLabel(selectedDate);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load data validation stats', error);
        },
      });
  }

  private loadVerificationStats(selectedDate: string, generatorId: number, cellKey: string): void {
    const monthValue = this.getSelectedMonthValue();
    if (!monthValue || !selectedDate) {
      return;
    }

    const requestedDate = selectedDate;
    const requestedCellKey = cellKey;
    this.dataVerificationDate = requestedDate;
    const payload = {
      data_date: buildReportDateValue(monthValue),
      owner: this.getSelectedOwnerValue(),
      generator_id: generatorId,
      selected_date: requestedDate,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_VERIFICATION, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const payloadValue = this.extractResponseValue<DataValidationVerificationResponse>(response);
          if (
            !payloadValue ||
            this.dataVerificationDate !== requestedDate ||
            payloadValue.selected_date !== requestedDate ||
            payloadValue.station_id !== generatorId
          ) {
            return;
          }

          this.dataVerification = payloadValue.data_verification ?? {
            actual_capacity: 0,
            generating_capacity: 0,
            verification: 0,
          };
          this.blockVariationDetailsByCellKey[requestedCellKey] = payloadValue.block_variations ?? [];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load data validation verification', error);
          this.dataVerification = { actual_capacity: 0, generating_capacity: 0, verification: 0 };
          this.blockVariationDetailsByCellKey[requestedCellKey] = [];
          this.cdr.markForCheck();
        },
      });
  }

  private formatCurrentDateLabel(referenceDate = new Date()): string {
    const month = String(referenceDate.getMonth() + 1).padStart(2, '0');
    const year = String(referenceDate.getFullYear()).slice(-2);
    return `${month}-${year}`;
  }


  private buildDaysFromMonth(monthValue: string): DataValidationDayDefinition[] {
    if (!/^\d{4}-\d{2}$/.test(monthValue)) {
      return [];
    }

    const [year, month] = monthValue.split('-').map((value) => Number(value));
    const lastDay = new Date(year, month, 0).getDate();
    const kathmanduToday = this.getKathmanduDateParts();
    const isCurrentMonth = year === kathmanduToday.year && month === kathmanduToday.month;
    const visibleLastDay = isCurrentMonth ? Math.min(lastDay, kathmanduToday.day) : lastDay;

    return Array.from({ length: visibleLastDay }, (_, index) => {
      const day = index + 1;
      return {
        day,
        date: `${monthValue}-${String(day).padStart(2, '0')}`,
        label: String(day),
        is_today: isCurrentMonth && day === kathmanduToday.day,
      };
    });
  }

  private extractResponseValue<T>(response: unknown): T | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const payload = response as { value?: unknown };
    return (payload.value ?? response) as T;
  }

  private getSelectedMonthValue(): string {
    const value = this.filtersForm.get('month')?.value;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    return getCurrentMonthValue();
  }

  private createCellKey(stationId: number, day: number): string {
    return `${stationId}-${day}`;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 5,
    }).format(value);
  }

  protected formatVerificationValue(value: number | null | undefined, suffix = ''): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'N/A';
    }

    const displayValue = suffix === '%' ? Math.abs(value) : value;
    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(displayValue);

    return `${formatted}${suffix}`;
  }

  protected getVerificationMetricValue(metricKey: 'actual_capacity' | 'generating_capacity' | 'verification'): number | null | undefined {
    return this.dataVerification[metricKey];
  }

  private formatDateLabel(value: string): string {
    if (this.currentDateMode === 'BS') {
      return this.formatBsDayLabel(value) || value;
    }

    return buildDayLabel(value) || value;
  }

  private formatMonthValueLabel(monthValue: string): string {
    if (this.currentDateMode === 'BS') {
      return this.formatBsMonthLabel(monthValue) || monthValue;
    }

    return buildMonthLabel(monthValue) || monthValue;
  }

  private formatBsMonthLabel(monthValue: string): string {
    const normalized = this.normalizeDateValue(monthValue);
    if (!normalized) {
      return '';
    }

    const match = /^(\d{4})-(\d{2})$/.exec(normalized);
    if (!match) {
      return '';
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const monthLabel = BS_MONTH_OPTIONS.find((option) => option.value === month)?.label ?? '';
    if (!monthLabel || !Number.isFinite(year)) {
      return '';
    }

    return `${monthLabel} ${year}`;
  }

  private formatBsDayLabel(dateValue: string): string {
    const normalized = this.normalizeDateValue(dateValue);
    if (!normalized) {
      return '';
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
    if (!match) {
      return '';
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const monthLabel = BS_MONTH_OPTIONS.find((option) => option.value === month)?.label ?? '';
    if (!monthLabel || !Number.isFinite(year) || !Number.isFinite(day)) {
      return '';
    }

    return `${monthLabel} ${String(day).padStart(2, '0')}, ${year}`;
  }

  private getKathmanduDateParts(referenceDate = new Date()): { year: number; month: number; day: number; hour: number; minute: number } {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kathmandu',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(referenceDate);
    const valueMap = parts.reduce<Record<string, string>>((accumulator, part) => {
      if (part.type !== 'literal') {
        accumulator[part.type] = part.value;
      }
      return accumulator;
    }, {});

    return {
      year: Number(valueMap['year']) || referenceDate.getFullYear(),
      month: Number(valueMap['month']) || referenceDate.getMonth() + 1,
      day: Number(valueMap['day']) || referenceDate.getDate(),
      hour: Number(valueMap['hour']) || referenceDate.getHours(),
      minute: Number(valueMap['minute']) || referenceDate.getMinutes(),
    };
  }

  private async resolveDateForCurrentMode(dateValue: string): Promise<{ selectedDate: string; selectedDateLabel: string } | null> {
    const conversion = await this.convertDataValidationDate(dateValue, this.getDatePreferenceKey());
    if (!conversion) {
      return null;
    }

    return {
      selectedDate: conversion.selected_date,
      selectedDateLabel: conversion.selected_date_label,
    };
  }


protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
  handleReportAutocompleteSearch({
    query: event?.query,
    form: this.filtersForm,
    controlName: 'substation_id',
    normalizeNumber: (value) => this.normalizeNumber(value),
    filterOptions: (query) => this.filterSubstationAutocomplete(query),
    findOption: (label) => this.findSubstationByLabel(label),
  });
}

protected onSubstationSearchBlur(): void {
  this.commitSubstationAutocompleteValue();
}

private loadSubstationOptions(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, '', false, { suppressToast: true })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response) => {
        this.substationOptions = this.extractSubstationOptions(response);
        this.refreshOwnerOptions();
        this.applySubstationAutocompleteFilters(this.substationSearchTerm);
        this.cdr.markForCheck();
      },
      error: () => {
        this.substationOptions = [];
        this.filteredSubstationOptions = [];
        this.cdr.markForCheck();
      },
    });
}

private filterSubstationAutocomplete(query: string): void {
  this.applySubstationAutocompleteFilters(query);
}

private commitSubstationAutocompleteValue(): void {
  commitReportAutocompleteValue({
    searchTerm: this.substationSearchTerm,
    form: this.filtersForm,
    controlName: 'substation_id',
    normalizeNumber: (value) => this.normalizeNumber(value),
    findOption: (label) => this.findSubstationByLabel(label),
    setSearchTerm: (value) => {
      this.substationSearchTerm = value;
    },
  });
}

private findSubstationByLabel(label: string): DataValidationSubstationOption | null {
  return findReportOptionByLabel(this.filteredSubstationOptions, this.substationOptions, label);
}

private extractSubstationOptions(response: unknown): DataValidationSubstationOption[] {
    const value = this.extractResponseValue<{ generators?: unknown[] }>(response);
    const rows = Array.isArray(value?.generators) ? value.generators : [];
    return rows
      .map((item) => {
      const record = item as Record<string, unknown>;
      return {
        id: Number(record['id']) || 0,
        name: typeof record['generator_name'] === 'string' ? record['generator_name'] : '-',
        station_code: typeof record['scada_station_code'] === 'string' ? record['scada_station_code'] : null,
        owner: typeof record['owner'] === 'string' ? record['owner'] : null,
      };
      })
      .filter((option) => this.hasScadaCode(option));
  }

  private applySubstationAutocompleteFilters(query: string): void {
    const selectedOwner = this.getSelectedOwnerValue();
    const ownerFilteredOptions = this.substationOptions.filter((option) => {
      if (!this.hasScadaCode(option)) {
        return false;
      }

      return !selectedOwner || this.matchesOwnerFilter(option.owner, selectedOwner);
    });
    this.filteredSubstationOptions = filterReportOptions(ownerFilteredOptions, query);
  }

  private refreshOwnerOptions(): void {
    const uniqueOwners = new Map<string, string>();
    for (const option of this.substationOptions) {
      const owner = typeof option.owner === 'string' ? option.owner.trim() : '';
      if (!owner) {
        continue;
      }

      const normalizedOwner = owner.toLowerCase();
      if (!uniqueOwners.has(normalizedOwner)) {
        uniqueOwners.set(normalizedOwner, owner);
      }
    }

    this.ownerOptions = [
      { label: 'All Owners', value: DATA_VALIDATION_OWNER_ALL_VALUE },
      ...Array.from(uniqueOwners.values()).map((owner) => ({ label: owner, value: owner })),
    ];
  }

  private hasScadaCode(option: DataValidationSubstationOption): boolean {
    return Boolean((option.station_code ?? '').trim());
  }

  private matchesOwnerFilter(optionOwner: string | null | undefined, selectedOwner: string): boolean {
    const normalizedOptionOwner = typeof optionOwner === 'string' ? optionOwner.trim().toLowerCase() : '';
    const normalizedSelectedOwner = selectedOwner.trim().toLowerCase();
    if (!normalizedOptionOwner || !normalizedSelectedOwner) {
      return false;
    }

    if (normalizedOptionOwner === normalizedSelectedOwner) {
      return true;
    }

    const ownerAliases = new Map<string, string[]>([
      ['nea subsidiary', ['nea sub', 'nea subsidiary']],
      ['nea sub', ['nea sub', 'nea subsidiary']],
    ]);
    const selectedAliases = ownerAliases.get(normalizedSelectedOwner) ?? [normalizedSelectedOwner];
    return selectedAliases.includes(normalizedOptionOwner);
  }

  private getSelectedOwnerValue(): string | null {
    const value = this.filtersForm.get('owner')?.value;
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    if (!normalized || normalized.toLowerCase() === DATA_VALIDATION_OWNER_ALL_VALUE) {
      return null;
    }

    return normalized;
  }

  private getSelectedAvailabilityValue(): DataValidationStatus | null {
    const selectedAvailability = this.availabilityControl.value ?? DATA_VALIDATION_AVAILABILITY_ALL_VALUE;
    if (selectedAvailability === DATA_VALIDATION_AVAILABILITY_ALL_VALUE) {
      return null;
    }

    return selectedAvailability;
  }

private normalizeNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

  private setupLiveBlockTicker(): void {
    interval(30000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  private getCurrentBlockNo(): number | null {
    if (this.currentBlockNo !== null) {
      return this.currentBlockNo;
    }

    if (!this.blocks.length) {
      return null;
    }

    const kathmanduNow = this.getKathmanduDateParts();
    const currentMinutes = kathmanduNow.hour * 60 + kathmanduNow.minute;

    const blocksWithMinutes = this.blocks
      .map((block) => {
        const match = /^(\d{1,2}):(\d{2})/.exec(block.label?.trim() ?? '');
        if (!match) {
          return null;
        }

        return {
          block,
          minutes: Number(match[1]) * 60 + Number(match[2]),
        };
      })
      .filter((item): item is { block: DataValidationBlockDefinition; minutes: number } => item !== null)
      .sort((a, b) => a.minutes - b.minutes);

    if (!blocksWithMinutes.length) {
      return this.blocks[0].block_no;
    }

    for (const item of blocksWithMinutes) {
      if (item.minutes >= currentMinutes) {
        return item.block.block_no;
      }
    }

    return blocksWithMinutes[blocksWithMinutes.length - 1].block.block_no;
  }

  protected getCurrentBlockStatus(row: DataValidationSubstationRow): 'available' | 'unavailable' {
    const currentDay = this.days.find((d) => d.is_today)?.day;
    if (currentDay === undefined) {
      return 'unavailable';
    }

    const cell = row.cells.find((c) => c.day === currentDay);
    if (!cell) {
      return 'unavailable';
    }

    const currentBlockNo = this.getCurrentBlockNo();
    if (currentBlockNo === null) {
      return 'unavailable';
    }

    const block = cell.blocks.find((b) => b.block_no === currentBlockNo);
    return block && block.value !== null && block.value !== undefined ? 'available' : 'unavailable';
  }

  protected getCurrentBlockBadgeClass(row: DataValidationSubstationRow): string {
    const status = this.getCurrentBlockStatus(row);
    return [
      'data-validation-table__live-badge',
      status === 'available'
        ? 'data-validation-table__live-badge--available'
        : 'data-validation-table__live-badge--unavailable',
    ].join(' ');
  }

  protected getCurrentBlockBadgeTitle(row: DataValidationSubstationRow): string {
    return this.getCurrentBlockStatus(row) === 'available' ? 'Data Available' : 'Data Not Available';
  }

  protected getDatePreferenceKey(): 'AD' | 'BS' {
    return this.preferencesSvc.showBsDate ? 'BS' : 'AD';
  }

  protected getBlockVariationClass(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): string {
    const detail = this.getBlockVariationDetail(row, cell, blockNo);
    return detail?.highlighted ? 'data-validation-detail-table__block-cell--variation' : '';
  }

  protected isBlockVariationHighlighted(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): boolean {
    return this.getBlockVariationClass(row, cell, blockNo) !== '';
  }

  protected getBlockVariationTooltip(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): string {
    return this.getBlockVariationDetail(row, cell, blockNo)?.tooltip_text ?? '';
  }

  private listenToPreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }

        void this.handleDateSystemChange();
      });
  }

  private async handleDateSystemChange(): Promise<void> {
    await this.syncMonthYearFilterMode();
    this.loadReport(this.pagination.page_no);
  }
}
